import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AgMark from '../components/brand/AgMark';
import TI from '../components/brand/TI';

/* NotFoundPage — light editorial 404 surface. Also rendered by AdminRoute
 * for any non-admin visitor hitting /assessor/* or /admin/* (hide-on-
 * anonymous policy), so it must read as the SAME visual system as
 * EnterpriseLanding — never as a "private app" leak.
 */

const SLATE = '#0B1426';
const MUTED = '#5B6577';
const HAIRLINE = '#ECECEC';
const PAPER = '#FFFFFF';
const CORAL = '#E63946';

export default function NotFoundPage() {
  const { user, isAdmin } = useAuth();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-5 sm:px-8"
      style={{
        backgroundColor: PAPER,
        color: SLATE,
        fontFamily: '"Inter", "Helvetica Neue", sans-serif',
      }}
      data-testid="not-found-page"
    >
      <div className="max-w-lg text-center">
        <div className="flex justify-center mb-7">
          <AgMark size={56} variant="light" />
        </div>
        <p
          className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] mb-6"
          style={{ color: MUTED }}
        >
          <TI color="#E63946" />
          <span style={{ color: MUTED }}> · Aᴳ Enterprise Portal</span>
        </p>
        <h1
          className="font-light tabular-nums mb-5"
          style={{
            color: SLATE,
            fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
            fontSize: 'clamp(4.5rem, 14vw, 7.5rem)',
            lineHeight: 1,
          }}
        >
          404
        </h1>
        <p
          className="text-base sm:text-lg leading-relaxed mb-10 px-2"
          style={{ color: MUTED }}
        >
          The page you're looking for isn't available. If you believe this is
          a mistake, contact your Aᴳ account team.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={user ? (isAdmin ? '/assessor' : '/dashboard') : '/'}
            data-testid="not-found-home"
            className="inline-flex items-center justify-center h-12 px-7 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] transition-colors"
            style={{ backgroundColor: CORAL, color: '#FFFFFF' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#C9192C'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CORAL; }}
          >
            {user ? 'Return to portal →' : 'Return to landing →'}
          </Link>
          {!user && (
            <Link
              to="/login"
              data-testid="not-found-login"
              className="inline-flex items-center justify-center h-12 px-7 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] transition-all"
              style={{
                backgroundColor: PAPER,
                color: SLATE,
                border: `1px solid ${HAIRLINE}`,
                boxShadow: '0 1px 0 rgba(11,20,38,0.02)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D6D6D6';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(11,20,38,0.06)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = HAIRLINE;
                e.currentTarget.style.boxShadow = '0 1px 0 rgba(11,20,38,0.02)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Client Sign In
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
